import { Card, CardContent, CardHeader, CardTitle } from '@xhubio-saas/core-frontend/ui-components'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { trpc } from '@/trpc'

type Status = 'pending' | 'ok' | 'invalid'

/**
 * Öffentliche Abmelde-Seite (`/unsubscribe`).
 *
 * Zwei Wege führen hierher:
 * - `?token=…` — der Link aus der Mail zeigt direkt auf diese Seite. Die
 *   Abmeldung passiert dann hier über `emailPreferences.unsubscribe`
 *   (öffentliche Prozedur, die den Token gegen die gespeicherte Zeile prüft).
 * - `?status=ok|invalid` — das Backend hat schon abgemeldet
 *   (`GET /api/unsubscribe`, RFC-8058-Weg) und leitet nur noch hierher weiter.
 */
export function UnsubscribePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const statusParam = searchParams.get('status')

  const [result, setResult] = useState<'ok' | 'invalid' | null>(null)
  const utils = trpc.useUtils()

  // Genau einmal auslösen — StrictMode rendert Effekte im Dev doppelt. Bewusst
  // der rohe Client statt useMutation: dessen Subscription überlebt den
  // StrictMode-Doppellauf hier nicht (Promise resolved, Hook-State blieb
  // "pending" — per QA-Sonde nachgewiesen). Das eigene setState im .then ist
  // davon unabhängig.
  const submitted = useRef(false)
  useEffect(() => {
    if (submitted.current) return
    if (!token || statusParam) return
    submitted.current = true
    utils.client.emailPreferences.unsubscribe
      .mutate({ token })
      .then(() => setResult('ok'))
      .catch(() => setResult('invalid'))
  }, [token, statusParam, utils])

  const status: Status = (() => {
    if (statusParam === 'ok') return 'ok'
    if (statusParam === 'invalid') return 'invalid'
    if (!token) return 'invalid'
    return result ?? 'pending'
  })()

  return (
    <Card className="w-full max-w-xl mx-auto" data-testid="unsubscribe-page">
      <CardHeader>
        <CardTitle>{t('unsubscribe.title', 'Unsubscribe')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {status === 'pending' && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t('unsubscribe.pending', 'Processing your request …')}</span>
          </div>
        )}

        {status === 'ok' && (
          <div className="flex items-start gap-2" data-testid="unsubscribe-success">
            <CheckCircle2 className="h-5 w-5 text-success" />
            <p>{t('unsubscribe.success', 'You will no longer receive product emails.')}</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="flex items-start gap-2" data-testid="unsubscribe-invalid">
            <XCircle className="h-5 w-5 text-destructive" />
            <p>
              {t(
                'unsubscribe.invalid',
                'This unsubscribe link is invalid or has already been used.'
              )}
            </p>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {t('unsubscribe.manageHint', 'You can manage your email settings any time:')}{' '}
          <Link to="/settings/email" className="underline underline-offset-4">
            {t('settings.email.tab', 'Email settings')}
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
